import React, { useState, useEffect } from 'react';
import { X, Mail, Send, Loader2, XCircle, DollarSign } from 'lucide-react';
import { api } from '../../services/api';
import { formatPickup, formatPickupDate, formatPickupTime } from '../../utils/datetime';

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700', bidding: 'bg-yellow-100 text-yellow-700',
  booked: 'bg-emerald-100 text-emerald-700', completed: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-red-100 text-red-700',
};
const STATUS_LABELS = {
  open: 'Offen', bidding: 'Angebote', booked: 'Gebucht', completed: 'Abgeschlossen', cancelled: 'Storniert',
};

export function TransferDetailModal({ transfer, onClose, resendEmail, resendingEmail, onRefresh }) {
  const [offers, setOffers] = useState(transfer?.offers || []);
  const [offersLoading, setOffersLoading] = useState(false);

  useEffect(() => {
    if (transfer?.id && (!transfer.offers || transfer.offers.length === 0) && transfer.offers_count > 0) {
      loadOffers();
    } else if (transfer?.offers) {
      setOffers(transfer.offers);
    }
  }, [transfer?.id]);

  const loadOffers = async () => {
    setOffersLoading(true);
    try {
      const r = await api.get(`/admin/transfer/${transfer.id}/offers`);
      setOffers(r.data || []);
    } catch (e) {
      console.error('Failed to load offers:', e);
    } finally { setOffersLoading(false); }
  };

  if (!transfer) return null;

  const handleCancel = async (refund) => {
    const msg = refund ? 'Transfer stornieren und Rückerstattung prüfen?' : 'Transfer ohne Rückerstattung stornieren?';
    if (!window.confirm(msg)) return;
    try {
      const res = await api.post(`/admin/transfer/${transfer.id}/cancel`, { refund, reason: refund ? 'Admin-Stornierung' : 'Admin-Stornierung ohne Rückerstattung' });
      if (refund) alert(`Transfer storniert. Rückerstattung: ${res.data.refund_eligible ? 'Berechtigt (>24h)' : 'Nicht berechtigt (<24h)'}`);
      else alert('Transfer storniert (ohne Rückerstattung)');
      onClose();
      onRefresh();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  const handleRefund = async () => {
    if (!window.confirm('Rückerstattung als abgeschlossen markieren?')) return;
    try {
      await api.post(`/admin/transfer/${transfer.id}/process-refund`);
      alert('Rückerstattung abgeschlossen');
      onClose();
      onRefresh();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="font-bold text-slate-800">Transfer Details</h2>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[transfer.status]}`}>{STATUS_LABELS[transfer.status]}</span>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} className="text-slate-400" /></button>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Kunde</p>
            <p className="font-medium text-slate-800">{transfer.customer_name}</p>
            <p className="text-sm text-slate-500">{transfer.customer_email} | {transfer.customer_phone}</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase font-semibold mb-2">Route</p>
            <div className="flex items-start gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full bg-orange-500 mt-1" /><p className="text-sm text-slate-700">{transfer.pickup_location?.address}</p></div>
            <div className="flex items-start gap-2"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500 mt-1" /><p className="text-sm text-slate-700">{transfer.dropoff_location?.address}</p></div>
            <div className="flex gap-4 mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
              <span>{transfer.distance_km} km</span><span>~{transfer.duration_minutes} Min.</span><span>{formatPickup(transfer.pickup_date)}</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400">Passagiere</p><p className="font-medium text-slate-800">{transfer.adults + transfer.children}</p></div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400">Gepäck</p><p className="font-medium text-slate-800">{transfer.luggage}</p></div>
            <div className="bg-slate-50 rounded-xl p-3"><p className="text-xs text-slate-400">Flugnummer</p><p className="font-medium text-slate-800">{transfer.flight_number || '-'}</p></div>
          </div>

          {/* Email Resend Actions */}
          {transfer.status === 'booked' && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="resend-email-section">
              <p className="text-xs text-blue-600 uppercase font-semibold mb-2">E-Mail erneut senden</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => resendEmail(transfer.id, 'booking', 'customer')} disabled={resendingEmail === `${transfer.id}-booking-customer`}
                  className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 disabled:opacity-50" data-testid="resend-booking-customer-btn">
                  {resendingEmail === `${transfer.id}-booking-customer` ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Buchungsbestätigung an Kunde
                </button>
                <button onClick={() => resendEmail(transfer.id, 'booking', 'driver')} disabled={resendingEmail === `${transfer.id}-booking-driver`}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50" data-testid="resend-booking-driver-btn">
                  {resendingEmail === `${transfer.id}-booking-driver` ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Buchungsbestätigung an Fahrer
                </button>
              </div>
            </div>
          )}

          {(transfer.status === 'bidding' || (transfer.status === 'open' && transfer.offers_count > 0)) && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="resend-offer-section">
              <p className="text-xs text-orange-600 uppercase font-semibold mb-2">Angebots-E-Mail erneut senden</p>
              <button onClick={() => resendEmail(transfer.id, 'offer')} disabled={resendingEmail === `${transfer.id}-offer-`}
                className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50" data-testid="resend-offer-btn">
                {resendingEmail === `${transfer.id}-offer-` ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                Angebote an Kunde senden ({transfer.offers_count} Angebote)
              </button>
            </div>
          )}

          {/* Cancel & Refund */}
          {transfer.status !== 'cancelled' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="transfer-cancel-section">
              <p className="text-xs text-red-600 uppercase font-semibold mb-2">Stornierung</p>
              <div className="flex gap-2">
                <button onClick={() => handleCancel(true)} className="flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600" data-testid="cancel-transfer-btn">
                  <XCircle size={14} /> Stornieren mit Rückerstattung
                </button>
                <button onClick={() => handleCancel(false)} className="flex items-center gap-1.5 px-4 py-2 bg-slate-500 text-white rounded-lg text-sm font-medium hover:bg-slate-600" data-testid="cancel-no-refund-btn">
                  <XCircle size={14} /> Stornieren ohne Rückerstattung
                </button>
              </div>
            </div>
          )}

          {transfer.status === 'cancelled' && transfer.refund_status === 'pending' && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4" data-testid="refund-section">
              <p className="text-xs text-orange-600 uppercase font-semibold mb-2">Rückerstattung ausstehend</p>
              <button onClick={handleRefund} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600" data-testid="process-refund-btn">
                <DollarSign size={14} /> Rückerstattung durchführen
              </button>
            </div>
          )}
          {transfer.status === 'cancelled' && transfer.refund_status === 'completed' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs text-emerald-600 uppercase font-semibold mb-1">Rückerstattung abgeschlossen</p>
              <p className="text-sm text-emerald-700">Die Rückerstattung wurde am {transfer.refund_processed_at ? new Date(transfer.refund_processed_at).toLocaleDateString('de-DE') : '-'} durchgeführt.</p>
            </div>
          )}
          {transfer.status === 'cancelled' && transfer.refund_status === 'not_eligible' && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <p className="text-xs text-slate-600 uppercase font-semibold mb-1">Keine Rückerstattung</p>
              <p className="text-sm text-slate-700">Stornierung weniger als 24 Stunden vor dem Termin. Der Fahrer erhält den vollen Betrag{transfer.driver_compensation ? ` (${transfer.driver_compensation} ${transfer.driver_compensation_currency || 'EUR'})` : ''}.</p>
            </div>
          )}

          {/* Offers */}
          {offersLoading && (
            <div className="text-center py-4"><Loader2 size={20} className="animate-spin text-orange-500 mx-auto" /><p className="text-xs text-slate-400 mt-1">Angebote laden...</p></div>
          )}
          {offers.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 uppercase font-semibold mb-3">Angebote ({offers.length})</p>
              {offers.map(o => {
                const offerDate = o.created_at ? new Date(o.created_at) : null;
                return (
                <div key={o.id} className={`rounded-xl p-4 mb-3 border ${o.status === 'accepted' ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-800">{o.driver_name}</p>
                      <p className="text-xs text-slate-400">{o.vehicle?.make} {o.vehicle?.model} {o.vehicle?.plate_number ? `(${o.vehicle.plate_number})` : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-xl text-slate-800">{o.price} <span className="text-sm font-normal text-slate-500">{o.currency || 'EUR'}</span></p>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${o.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : o.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {o.status === 'accepted' ? 'Akzeptiert' : o.status === 'rejected' ? 'Abgelehnt' : 'Ausstehend'}
                      </span>
                    </div>
                  </div>
                  {/* Transfer summary in offer */}
                  <div className="grid grid-cols-4 gap-2 mt-2 pt-2 border-t border-slate-200/60">
                    <div><p className="text-[10px] text-slate-400 uppercase">Datum</p><p className="text-xs font-medium text-slate-700">{formatPickupDate(transfer.pickup_date) || '-'}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Uhrzeit</p><p className="text-xs font-medium text-slate-700">{formatPickupTime(transfer.pickup_date) || '-'}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Personen</p><p className="text-xs font-medium text-slate-700">{transfer.adults || 0}{transfer.children ? ` + ${transfer.children} Ki.` : ''}</p></div>
                    <div><p className="text-[10px] text-slate-400 uppercase">Gepäck</p><p className="text-xs font-medium text-slate-700">{transfer.luggage || 0}</p></div>
                  </div>
                  {o.notes && <p className="text-xs text-slate-500 mt-2 bg-white/60 p-2 rounded border border-slate-100 italic">"{o.notes}"</p>}
                  {offerDate && <p className="text-[10px] text-slate-400 mt-2">Angebot vom {offerDate.toLocaleDateString('de-DE')} um {offerDate.toLocaleTimeString('de-DE', {hour:'2-digit',minute:'2-digit'})}</p>}
                </div>
                );
              })}
            </div>
          )}
          {!offersLoading && offers.length === 0 && transfer.offers_count > 0 && (
            <p className="text-xs text-slate-400 text-center py-3">Angebote konnten nicht geladen werden.</p>
          )}
        </div>
      </div>
    </div>
  );
}
